import { useReveal } from "@/hooks/useReveal";
import { ShieldCheck, Atom, Lock, BarChart3 } from "lucide-react";
import { LQIP } from "@/constants/lqip";

/**
 * Section 02 — The Platform.
 * The high-fidelity closed-loop diagram IS the section header. No React title
 * since the image already contains the title and "Capture signal → map circuits…" line.
 * The 4 trust pills sit below as scannable HTML (good for SEO/crawlers and a11y).
 */
const FEATURES = [
  {
    icon: ShieldCheck,
    title: "Biocompatible by design",
    body: "Soft interface concepts aimed at long-term signal compatibility.",
    color: "#38BDF8",
  },
  {
    icon: Atom,
    title: "Physically grounded AI",
    body: "Models constrained by biological priors and uncertainty.",
    color: "#6366F1",
  },
  {
    icon: Lock,
    title: "Privacy & security by default",
    body: "Architecture designed around research-grade data protection.",
    color: "#8B5CF6",
  },
  {
    icon: BarChart3,
    title: "Built for research. Designed to scale.",
    body: "Modular pipeline targeted at continuous data growth.",
    color: "#38BDF8",
  },
];

export function PlatformSection() {
  useReveal();
  return (
    <section
      id="platform"
      className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-28 lg:py-36"
    >
      <div className="reveal flex items-center gap-3 mb-10">
        <span className="section-num">02</span>
        <span className="h-px w-10 bg-white/15" />
        <span className="section-num !text-[#38BDF8]">The Platform</span>
      </div>

      <figure
        className="reveal relative rounded-3xl overflow-hidden border border-white/10 bg-[#040A18]"
        style={{
          backgroundImage: `url(${LQIP.closed_loop_platform})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <picture>
          <source srcSet="/assets/generated/closed_loop_platform.avif" type="image/avif" />
          <source srcSet="/assets/generated/closed_loop_platform.webp" type="image/webp" />
          <img
            src="/assets/generated/closed_loop_platform.png"
            alt="Closed-Loop Neuroplasticity Platform — capture signal, map circuits, model activity, guide adaptive feedback. Five-stage pipeline: Interface, Mapping, Modeling, Feedback, Tracking."
            loading="lazy"
            decoding="async"
            className="w-full h-auto"
          />
        </picture>
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(900px 240px at 50% 100%, rgba(99,102,241,0.18), transparent 70%)",
          }}
        />
      </figure>

      {/* Feature pills below (mirrors the diagram's four pills for SEO + a11y) */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {FEATURES.map(({ icon: Icon, title, body, color }, i) => (
          <article
            key={i}
            className="reveal glass rounded-2xl p-6 group hover:-translate-y-1 transition-all duration-500"
            style={{ transitionDelay: `${i * 60}ms` }}
          >
            <div
              className="h-10 w-10 rounded-xl grid place-items-center"
              style={{
                background: `${color}1A`,
                border: `1px solid ${color}44`,
                color,
              }}
            >
              <Icon size={18} strokeWidth={1.7} />
            </div>
            <h3 className="mt-5 font-display text-white text-[17px] leading-snug font-medium">
              {title}
            </h3>
            <p className="mt-2 text-[13px] text-[#94A3B8] leading-relaxed">
              {body}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}
